/**
 * RecognitionRuleRepository - Database operations for recognitionRules collection
 */

import { BaseRepository, where } from '../../discipline/repositories/BaseRepository';
import { collection, query as firestoreQuery, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';

export class RecognitionRuleRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - recognitionRules are stored at schools/{schoolId}/recognitionRules/{ruleId}
    super('recognitionRules', { isHierarchical: true });
  }

  /**
   * Query hierarchical collection ONLY - migration completed
   */
  async queryBothPaths(constraints = [], schoolId = null) {
    console.log('[RecognitionRuleRepository] 🔍 querying hierarchical path', { schoolId, constraintsCount: constraints.length });

    if (!schoolId) {
      console.error('[RecognitionRuleRepository] ❌ schoolId required for hierarchical query');
      return [];
    }

    try {
      console.log('[RecognitionRuleRepository] 📂 Querying: schools/' + schoolId + '/recognitionRules');
      const hierarchicalRef = collection(db, 'schools', schoolId, 'recognitionRules');
      const hierarchicalQuery = firestoreQuery(hierarchicalRef, ...constraints);
      const hierarchicalSnapshot = await getDocs(hierarchicalQuery);
      console.log('[RecognitionRuleRepository] ✅ Query returned', hierarchicalSnapshot.size, 'docs');

      return hierarchicalSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('[RecognitionRuleRepository] ⚠️ Hierarchical query failed:', error.message);
      return [];
    }
  }

  /**
   * Get all active rules
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of active rules
   */
  async getActiveRules(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for hierarchical recognitionRules collection');
    }

    const constraints = [
      where('active', '==', true)
    ];

    // Use queryBothPaths to query both hierarchical and flat collections
    const rules = await this.queryBothPaths(constraints, schoolId);

    // Sort client-side by sortOrder (lower numbers first)
    return rules.sort((a, b) => {
      const orderA = a.sortOrder ?? 999999;
      const orderB = b.sortOrder ?? 999999;
      return orderA - orderB;
    });
  }

  /**
   * Get rules by type
   * @param {string} type - Rule type (mention, accountability, threshold, etc.)
   * @param {boolean} activeOnly - Only return active rules
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of rules
   */
  async getRulesByType(type, activeOnly = true, schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for hierarchical recognitionRules collection');
    }

    const constraints = [
      where('type', '==', type)
    ];

    if (activeOnly) {
      constraints.push(where('active', '==', true));
    }

    // Use queryBothPaths to query both hierarchical and flat collections
    const rules = await this.queryBothPaths(constraints, schoolId);

    // Sort client-side by sortOrder (lower numbers first), then createdAt (newer first)
    return rules.sort((a, b) => {
      const orderDiff = (a.sortOrder ?? 999999) - (b.sortOrder ?? 999999);
      if (orderDiff !== 0) return orderDiff;

      const dateA = a.createdAt?.toMillis?.() || 0;
      const dateB = b.createdAt?.toMillis?.() || 0;
      return dateB - dateA;
    });
  }

  /**
   * Get mention rules for evaluation
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of active mention rules
   */
  async getRecognitionRules(schoolId) {
    return this.getRulesByType('recognition', true, schoolId);
  }

  /**
   * Get accountability rules for evaluation
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of active accountability rules
   */
  async getStreakRules(schoolId) {
    return this.getRulesByType('streak', true, schoolId);
  }

  /**
   * Get threshold rules for evaluation
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of active threshold rules
   */
  async getThresholdRules(schoolId) {
    return this.getRulesByType('threshold', true, schoolId);
  }

  /**
   * Get zero-tolerance rules for evaluation
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of active zero-tolerance rules
   */
  async getMilestoneRules(schoolId) {
    return this.getRulesByType('milestone', true, schoolId);
  }

  /**
   * Create new rule
   * @param {Object} ruleData - Rule data (must include schoolId)
   * @returns {Promise<Object>} Created rule
   */
  async createRule(ruleData) {
    if (!ruleData.schoolId) {
      throw new Error('schoolId is required in ruleData for hierarchical recognitionRules collection');
    }

    // Get max sort order for this type
    const existingRules = await this.getRulesByType(ruleData.type, false, ruleData.schoolId);
    const maxSortOrder = existingRules.reduce((max, rule) =>
      Math.max(max, rule.sortOrder || 0), 0
    );

    return this.create({
      ...ruleData,
      active: ruleData.active !== false, // Default to active
      sortOrder: ruleData.sortOrder !== undefined ? ruleData.sortOrder : maxSortOrder + 1,
      executionCount: 0,
      lastExecuted: null
    });
  }

  /**
   * Update rule
   * @param {string} ruleId - Rule ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated rule
   */
  async updateRule(ruleId, updates) {
    return this.update(ruleId, updates);
  }

  /**
   * Toggle rule active state
   * @param {string} ruleId - Rule ID
   * @returns {Promise<Object>} Updated rule
   */
  async toggleRuleActive(ruleId) {
    const rule = await this.getById(ruleId);

    if (!rule) {
      throw new Error(`Rule ${ruleId} not found`);
    }

    return this.update(ruleId, {
      active: !rule.active
    });
  }

  /**
   * Update rule sort orders
   * @param {Array} ruleOrders - Array of {id, sortOrder} objects
   * @returns {Promise<Array>} Updated rules
   */
  async updateSortOrders(ruleOrders) {
    const promises = ruleOrders.map(({ id, sortOrder }) =>
      this.update(id, { sortOrder })
    );

    return Promise.all(promises);
  }

  /**
   * Record rule execution
   * @param {string} ruleId - Rule ID
   * @param {Object} executionDetails - Details about the execution
   * @returns {Promise<Object>} Updated rule
   */
  async recordExecution(ruleId, executionDetails = {}) {
    const rule = await this.getById(ruleId);

    if (!rule) {
      throw new Error(`Rule ${ruleId} not found`);
    }

    return this.update(ruleId, {
      executionCount: (rule.executionCount || 0) + 1,
      lastExecuted: Timestamp.now(),
      lastExecutionDetails: executionDetails
    });
  }

  /**
   * Get rules for student evaluation
   * @param {Object} context - Evaluation context (student, class, schoolId, etc.)
   * @returns {Promise<Object>} Rules grouped by type
   */
  async getRulesForEvaluation(context = {}) {
    if (!context.schoolId) {
      throw new Error('schoolId is required in context for hierarchical recognitionRules collection');
    }

    const activeRules = await this.getActiveRules(context.schoolId);

    // Group rules by type
    const groupedRules = {
      recognition: [],
      streak: [],
      threshold: [],
      'milestone': [],
      contract: [],
      escalation: []
    };

    activeRules.forEach(rule => {
      if (groupedRules[rule.type]) {
        // Apply any context-specific filtering
        if (this.ruleAppliesToContext(rule, context)) {
          groupedRules[rule.type].push(rule);
        }
      }
    });

    return groupedRules;
  }

  /**
   * Check if rule applies to context
   * @param {Object} rule - Rule object
   * @param {Object} context - Evaluation context
   * @returns {boolean} True if rule applies
   */
  ruleAppliesToContext(rule, context) {
    // Check class restrictions
    if (rule.restrictToClasses && rule.restrictToClasses.length > 0) {
      if (!context.classId || !rule.restrictToClasses.includes(context.classId)) {
        return false;
      }
    }

    // Check student restrictions
    if (rule.restrictToStudents && rule.restrictToStudents.length > 0) {
      if (!context.studentId || !rule.restrictToStudents.includes(context.studentId)) {
        return false;
      }
    }

    // Check time restrictions
    if (rule.activeHours) {
      const now = new Date();
      const currentHour = now.getHours();
      if (currentHour < rule.activeHours.start || currentHour >= rule.activeHours.end) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get rule statistics
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Object>} Statistics object
   */
  async getRuleStatistics(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for hierarchical recognitionRules collection');
    }

    const allRules = await this.getAll(schoolId);

    const stats = {
      totalRules: allRules.length,
      activeRules: allRules.filter(r => r.active).length,
      rulesByType: {},
      mostExecuted: null,
      recentlyExecuted: []
    };

    // Count by type
    allRules.forEach(rule => {
      if (!stats.rulesByType[rule.type]) {
        stats.rulesByType[rule.type] = { total: 0, active: 0 };
      }
      stats.rulesByType[rule.type].total++;
      if (rule.active) {
        stats.rulesByType[rule.type].active++;
      }
    });

    // Find most executed
    const executed = allRules.filter(r => r.executionCount > 0);
    if (executed.length > 0) {
      stats.mostExecuted = executed.reduce((max, rule) =>
        rule.executionCount > max.executionCount ? rule : max
      );
    }

    // Find recently executed
    stats.recentlyExecuted = allRules
      .filter(r => r.lastExecuted)
      .sort((a, b) => b.lastExecuted.seconds - a.lastExecuted.seconds)
      .slice(0, 5);

    return stats;
  }

  /**
   * Clone a rule
   * @param {string} ruleId - Rule ID to clone
   * @param {Object} overrides - Fields to override in clone
   * @returns {Promise<Object>} Cloned rule
   */
  async cloneRule(ruleId, overrides = {}) {
    const originalRule = await this.getById(ruleId);

    if (!originalRule) {
      throw new Error(`Rule ${ruleId} not found`);
    }

    // Remove fields that shouldn't be cloned
    const { id, createdAt, updatedAt, executionCount, lastExecuted, ...ruleData } = originalRule;

    return this.createRule({
      ...ruleData,
      name: overrides.name || `${originalRule.name} (Copy)`,
      active: false, // Start cloned rules as inactive
      ...overrides
    });
  }

  /**
   * Get rules that haven't been executed recently
   * @param {number} days - Number of days to consider
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of unused rules
   */
  async getUnusedRules(days = 30, schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for hierarchical recognitionRules collection');
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const allRules = await this.getActiveRules(schoolId);

    return allRules.filter(rule => {
      if (!rule.lastExecuted) return true;

      const lastExecutedDate = rule.lastExecuted.toDate();
      return lastExecutedDate < cutoffDate;
    });
  }

  /**
   * Subscribe to rule changes
   * @param {Function} callback - Callback for changes
   * @param {Object} options - Subscription options (must include schoolId)
   * @returns {Function} Unsubscribe function
   */
  subscribeToRules(callback, options = {}) {
    if (!options.schoolId) {
      throw new Error('schoolId is required in options for hierarchical recognitionRules collection');
    }

    const constraints = [];

    if (options.type) {
      constraints.push(where('type', '==', options.type));
    }

    if (options.activeOnly) {
      constraints.push(where('active', '==', true));
    }

    // Don't use orderBy in subscription - sort client-side in callback to avoid index requirements

    return this.subscribe(callback, constraints, options.schoolId);
  }
}